import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  Stack,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { CheckCircleOutline, CelebrationOutlined } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

function CelebrationDialog({ open, onClose }) {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          margin: 2,
          borderRadius: 3,
          overflow: "hidden",
          textAlign: "center",
        },
      }}
    >
      <DialogContent sx={{ p: 4 }}>
        {/* Celebration Icons */}
        <Box sx={{ mb: 3, position: "relative", display: "inline-block" }}>
          <CheckCircleOutline
            sx={{
              fontSize: 80,
              color: "success.main",
              mb: 1,
            }}
          />
          <CelebrationOutlined
            sx={{
              position: "absolute",
              top: -10,
              right: -10,
              fontSize: 32,
              color: "warning.main",
              animation: "bounce 2s infinite",
              "@keyframes bounce": {
                "0%, 20%, 50%, 80%, 100%": {
                  transform: "translateY(0)",
                },
                "40%": {
                  transform: "translateY(-10px)",
                },
                "60%": {
                  transform: "translateY(-5px)",
                },
              },
            }}
          />
        </Box>

        {/* Success Message */}
        <Typography
          variant="h5"
          component="div"
          fontWeight={700}
          color="success.main"
          gutterBottom
        >
          {t("subscription.paymentConfirmedTitle", "Payment Confirmed!")}
        </Typography>

        <Typography
          variant="body1"
          color="text.primary"
          sx={{ mb: 2, fontWeight: 500 }}
        >
          {t(
            "subscription.transferConfirmedMessage",
            "Your transfer has been confirmed and you now have full access to the platform."
          )}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          {t(
            "subscription.fullAccessMessage",
            "You can now create work permits, manage observations, and access all platform features without any limitations."
          )}
        </Typography>

        {/* Features List */}
        <Box
          sx={{
            bgcolor: alpha(theme.palette.success.main, 0.08),
            border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
            borderRadius: 2,
            p: 2,
            mb: 3,
          }}
        >
          <Typography
            variant="subtitle2"
            fontWeight={600}
            color="success.main"
            gutterBottom
          >
            {t("subscription.availableFeatures", "Available Features:")}
          </Typography>
          <Stack spacing={1} alignItems="flex-start">
            {[
              t("subscription.featureWorkPermits", "Work Permits"),
              t("subscription.featureObservations", "Safety Observations"),
              t("subscription.featureDocuments", "Document Management"),
              t("subscription.featureReporting", "Analytics & Reporting"),
              t("subscription.featureTeamManagement", "Team Management"),
            ].map((feature, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <CheckCircleOutline
                  sx={{ fontSize: 16, color: "success.main" }}
                />
                <Typography variant="body2" color="text.primary">
                  {feature}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>

        {/* Action Button */}
        <Button
          onClick={onClose}
          variant="contained"
          color="success"
          size="large"
          sx={{
            px: 4,
            py: 1.5,
            fontWeight: 600,
            borderRadius: 2,
            boxShadow: 2,
            "&:hover": {
              boxShadow: 4,
            },
          }}
        >
          {t("subscription.startUsingPlatform", "Start Using Platform")}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

CelebrationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default CelebrationDialog;
